/**
 * DynamicFormField — renders a single FormField as the appropriate Mantine input.
 *
 * Maps FormFieldType enum → Mantine component. Handles conditional visibility
 * using the ExposedWorkflow schema's conditionalFieldId/conditionalEquals.
 */
import {
  Checkbox,
  NumberInput,
  Radio,
  Select,
  TextInput,
  Textarea,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import type { FormField } from '../../data/portal/types';

interface DynamicFormFieldProps {
  field: FormField;
  value: unknown;
  onChange: (fieldId: string, value: unknown) => void;
  error?: string;
}

export function DynamicFormField({ field, value, onChange, error }: DynamicFormFieldProps) {
  const commonProps = {
    label: field.label,
    required: field.required,
    placeholder: field.placeholder ?? undefined,
    error,
  };

  const handleChange = (val: unknown) => onChange(field.id, val);

  switch (field.fieldType) {
    case 'TEXT':
      return (
        <TextInput
          {...commonProps}
          value={(value as string) ?? ''}
          onChange={(e) => handleChange(e.currentTarget.value)}
          minLength={field.minLength ?? undefined}
          maxLength={field.maxLength ?? undefined}
        />
      );

    case 'EMAIL':
      return (
        <TextInput
          {...commonProps}
          type="email"
          value={(value as string) ?? ''}
          onChange={(e) => handleChange(e.currentTarget.value)}
        />
      );

    case 'PHONE':
      return (
        <TextInput
          {...commonProps}
          type="tel"
          value={(value as string) ?? ''}
          onChange={(e) => handleChange(e.currentTarget.value)}
        />
      );

    case 'TEXT_AREA':
      return (
        <Textarea
          {...commonProps}
          value={(value as string) ?? ''}
          onChange={(e) => handleChange(e.currentTarget.value)}
          minRows={3}
          minLength={field.minLength ?? undefined}
          maxLength={field.maxLength ?? undefined}
        />
      );

    case 'SELECT':
      return (
        <Select
          {...commonProps}
          value={(value as string) ?? null}
          onChange={handleChange}
          data={field.options?.map((o) => ({ value: o.value, label: o.label })) ?? []}
        />
      );

    case 'MULTI_SELECT':
      return (
        <Select
          {...commonProps}
          value={(value as string) ?? null}
          onChange={handleChange}
          data={field.options?.map((o) => ({ value: o.value, label: o.label })) ?? []}
        />
      );

    case 'CHECKBOX':
      return (
        <Checkbox
          label={field.label}
          checked={!!value}
          onChange={(e) => handleChange(e.currentTarget.checked)}
          error={error}
        />
      );

    case 'RADIO':
      return (
        <Radio.Group
          {...commonProps}
          value={(value as string) ?? ''}
          onChange={handleChange}
        >
          {field.options?.map((o) => (
            <Radio key={o.value} value={o.value} label={o.label} mt="xs" />
          ))}
        </Radio.Group>
      );

    case 'DATE':
      return (
        <DateInput
          {...commonProps}
          value={(value as string) ?? null}
          onChange={(date) => handleChange(date ?? null)}
        />
      );

    case 'NUMBER':
      return (
        <NumberInput
          {...commonProps}
          value={(value as number) ?? ''}
          onChange={handleChange}
        />
      );

    case 'FILE_UPLOAD':
      return (
        <TextInput
          {...commonProps}
          value=""
          disabled
          placeholder="File upload coming soon"
        />
      );

    default:
      return (
        <TextInput
          {...commonProps}
          value={(value as string) ?? ''}
          onChange={(e) => handleChange(e.currentTarget.value)}
        />
      );
  }
}
